// Environment configuration - safely handle browser environment
const LOG_LEVEL = (typeof process !== 'undefined' ? process.env.LOG_LEVEL : import.meta.env.VITE_LOG_LEVEL) || 'info';
const LOG_DIR = (typeof process !== 'undefined' ? process.env.LOG_DIR : import.meta.env.VITE_LOG_DIR) || './logs';
const IS_PRODUCTION = (typeof process !== 'undefined' ? process.env.NODE_ENV : import.meta.env.MODE) === 'production';
const IS_BROWSER = typeof window !== 'undefined';
const TEST_LOGGING = (typeof process !== 'undefined' ? process.env.TEST_LOGGING : import.meta.env.VITE_TEST_LOGGING) === 'true';

// Log levels mapping
const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
} as const;

type LogLevel = keyof typeof LOG_LEVELS;

// Simple logger interface for browser
class BrowserLogger {
  private level: number;

  constructor(level: string = 'info') {
    this.level = LOG_LEVELS[level as LogLevel] || LOG_LEVELS.info;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] <= this.level;
  }

  private formatMessage(level: LogLevel, message: string, meta?: any): string {
    const timestamp = new Date().toISOString();
    const metaStr = meta ? ` ${JSON.stringify(meta)}` : '';
    return `${timestamp} [${level.toUpperCase()}] ${message}${metaStr}`;
  }

  debug(message: string, meta?: any) {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage('debug', message, meta));
    }
  }

  info(message: string, meta?: any) {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message, meta));
    }
  }

  warn(message: string, meta?: any) {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, meta));
    }
  }

  error(message: string, meta?: any) {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage('error', message, meta));
    }
  }
}

// For Node.js environments (server-side), create a proper Winston logger
let winston: any = null;
let logger: any = null;

if (!IS_BROWSER) {
  try {
    // Dynamic imports for Node.js only
    winston = require('winston');
    const DailyRotateFile = require('winston-daily-rotate-file');
    const path = require('path');
    const fs = require('fs');

    // Ensure log directory exists
    if (IS_PRODUCTION || TEST_LOGGING) {
      try {
        if (!fs.existsSync(LOG_DIR)) {
          fs.mkdirSync(LOG_DIR, { recursive: true });
        }
      } catch (error) {
        console.error('Failed to create log directory:', error);
      }
    }

    // Custom format with ISO-8601 timestamp and log level prefix
    const logFormat = winston.format.combine(
      winston.format.timestamp({ format: 'YYYY-MM-DDTHH:mm:ss.SSSZ' }),
      winston.format.printf(({ timestamp, level, message, ...meta }: any) => {
        const metaStr = Object.keys(meta).length ? ` ${JSON.stringify(meta)}` : '';
        return `${timestamp} [${level.toUpperCase()}] ${message}${metaStr}`;
      })
    );

    // Create transports array
    const transports: any[] = [];

    // In production or test mode: write to files with rotation
    if (IS_PRODUCTION || TEST_LOGGING) {
      // Combined log (info, debug, warn, error)
      transports.push(
        new DailyRotateFile({
          filename: path.join(LOG_DIR, 'combined-%DATE%.log'),
          datePattern: 'YYYY-MM-DD',
          zippedArchive: true,
          maxSize: '20m',
          maxFiles: '14d',
          level: 'debug',
          format: logFormat,
          handleExceptions: false,
          handleRejections: false,
        })
      );

      // Error log (warnings and errors only)
      transports.push(
        new DailyRotateFile({
          filename: path.join(LOG_DIR, 'error-%DATE%.log'),
          datePattern: 'YYYY-MM-DD',
          zippedArchive: true,
          maxSize: '20m',
          maxFiles: '14d',
          level: 'warn',
          format: logFormat,
          handleExceptions: false,
          handleRejections: false,
        })
      );
    } else {
      // In development: write to console (unchanged behavior)
      transports.push(
        new winston.transports.Console({
          format: winston.format.combine(
            winston.format.colorize(),
            winston.format.simple()
          ),
        })
      );
    }

    // Create the winston logger
    logger = winston.createLogger({
      level: LOG_LEVEL,
      transports,
      exitOnError: false,
    });

    // Handle uncaught exceptions and unhandled rejections
    if (IS_PRODUCTION || TEST_LOGGING) {
      process.on('uncaughtException', (error: Error) => {
        logger.error('Uncaught Exception', {
          error: error.message,
          stack: error.stack,
          name: error.name,
        });
        // Give logger time to write before exiting
        setTimeout(() => {
          process.exit(1);
        }, 1000);
      });

      process.on('unhandledRejection', (reason: any, promise: Promise<any>) => {
        logger.error('Unhandled Rejection', {
          reason: typeof reason === 'object' ? JSON.stringify(reason) : String(reason),
          promise: promise.toString(),
        });
      });
    }

    // Graceful shutdown handling
    const gracefulShutdown = () => {
      logger.info('Received shutdown signal, closing log streams...');
      
      // Close all transports
      logger.transports.forEach((transport: any) => {
        if (transport.close) {
          transport.close();
        }
      });

      // Give time for logs to be written
      setTimeout(() => {
        process.exit(0);
      }, 1000);
    };

    // Listen for termination signals
    process.on('SIGTERM', gracefulShutdown);
    process.on('SIGINT', gracefulShutdown);
    process.on('beforeExit', gracefulShutdown);

  } catch (error) {
    console.warn('Failed to initialize Winston logger, falling back to console:', error);
    logger = new BrowserLogger(LOG_LEVEL);
  }
} else {
  // Browser environment - use simple logger
  logger = new BrowserLogger(LOG_LEVEL);
}

// Override global console methods in production or test mode (Node.js only)
if ((IS_PRODUCTION || TEST_LOGGING) && !IS_BROWSER) {
  const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
  };

  // Redirect console methods to logger
  console.log = (...args: any[]) => {
    logger.info(args.map((arg: any) => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '));
  };

  console.info = (...args: any[]) => {
    logger.info(args.map((arg: any) => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '));
  };

  console.warn = (...args: any[]) => {
    logger.warn(args.map((arg: any) => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '));
  };

  console.error = (...args: any[]) => {
    logger.error(args.map((arg: any) => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '));
  };

  console.debug = (...args: any[]) => {
    logger.debug(args.map((arg: any) => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' '));
  };

  // Store reference to original methods for emergency use
  (globalThis as any).__originalConsole = originalConsole;
} else if (IS_PRODUCTION && IS_BROWSER) {
  // In browser production, silence console but don't redirect to files
  const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
  };

  // Silent console methods in browser production
  console.log = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.error = () => {};
  console.debug = () => {};

  // Store reference to original methods for emergency use
  (globalThis as any).__originalConsole = originalConsole;
}

// Export the logger and utility functions
export { logger };

// Convenience methods that match common console usage patterns
export const log = {
  debug: (message: string, meta?: any) => logger.debug(message, meta),
  info: (message: string, meta?: any) => logger.info(message, meta),
  warn: (message: string, meta?: any) => logger.warn(message, meta),
  error: (message: string, meta?: any) => logger.error(message, meta),
};

// Helper function to restore original console (for debugging/testing)
export const restoreConsole = () => {
  if (IS_PRODUCTION && (globalThis as any).__originalConsole) {
    const original = (globalThis as any).__originalConsole;
    console.log = original.log;
    console.info = original.info;
    console.warn = original.warn;
    console.error = original.error;
    console.debug = original.debug;
  }
};

export default logger;